import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { MessageCircle, Bell, Users, BarChart, Clock, Shield } from 'lucide-react';

const WhatsAppPage = () => {
  const features = [
    {
      icon: MessageCircle,
      title: 'Direct Communication',
      description: 'Connect with constituents on the platform they use daily. WhatsApp offers unmatched open rates and instant delivery.'
    },
    {
      icon: Bell,
      title: 'Timely Updates',
      description: 'Send real-time notifications about government schemes, local events, development updates, and emergency information.'
    },
    {
      icon: Users,
      title: 'Group Broadcasts',
      description: 'Segment your audience and send targeted messages to specific villages, interest groups, or demographic segments.'
    },
    {
      icon: BarChart,
      title: 'Feedback Collection',
      description: 'Conduct quick polls, gather opinions on initiatives, and collect feedback through simple, accessible surveys.'
    },
    {
      icon: Clock,
      title: 'Automated Responses',
      description: 'Set up automated replies for common queries, ensuring constituents receive instant acknowledgment 24/7.'
    },
    {
      icon: Shield,
      title: 'Secure & Trusted',
      description: 'Leverage WhatsApp\'s end-to-end encryption and the platform\'s trusted status among rural users for authentic communication.'
    }
  ];

  const useCases = [
    {
      title: 'Scheme Announcements',
      description: 'Instantly notify eligible constituents about new government schemes, application deadlines, and required documents.',
      example: 'Example: "New housing scheme launched! Apply by March 31st. Click here for details and eligibility."'
    },
    {
      title: 'Event Invitations',
      description: 'Send personalized invitations to community meetings, health camps, training programs, and public events.',
      example: 'Example: "You\'re invited to the Free Health Camp at Village Hall on Sunday, 10 AM. RSVP: Reply YES"'
    },
    {
      title: 'Emergency Alerts',
      description: 'Quickly disseminate critical information during emergencies like floods, health crises, or safety concerns.',
      example: 'Example: "ALERT: Heavy rainfall expected. Stay indoors. Emergency helpline: 1234567890"'
    },
    {
      title: 'Development Updates',
      description: 'Share progress on infrastructure projects, completed works, and upcoming initiatives to maintain transparency.',
      example: 'Example: "Road construction at Chowk completed! Next: Street lighting installation by month-end."'
    },
    {
      title: 'Feedback & Surveys',
      description: 'Collect opinions on local issues, gauge satisfaction with services, and understand community priorities.',
      example: 'Example: "How satisfied are you with water supply? Reply 1-Very Satisfied, 2-Satisfied, 3-Need Improvement"'
    },
    {
      title: 'Complaint Follow-ups',
      description: 'Automatically update constituents on the status of their registered complaints through the CRS system.',
      example: 'Example: "Your complaint #1234 about streetlight is now RESOLVED. Thank you for your patience!"'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white py-24">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <MessageCircle className="w-24 h-24 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-semibold mb-6 leading-tight">
            WhatsApp Integration
          </h1>
          <p className="text-2xl md:text-3xl mb-4">
            Direct, Trusted Communication
          </p>
          <p className="text-xl max-w-3xl mx-auto opacity-95">
            Reach your constituents on the platform they already trust and use daily.
          </p>
        </div>
      </section>

      {/* Why WhatsApp */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-8 text-[#1E3A8A] text-center">
            Why WhatsApp for Governance?
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-xl leading-relaxed text-[#374151] mb-6">
              In rural India, WhatsApp has become the primary mode of digital communication. With over <strong>95% open rates</strong> (compared to 20-30% for emails), it offers unparalleled reach and engagement.
            </p>
            <p className="text-xl leading-relaxed text-[#374151] mb-6">
              For public servants, WhatsApp integration means being present where your constituents already are—creating a sense of accessibility and personal connection that builds trust and loyalty.
            </p>
            <p className="text-xl leading-relaxed text-[#374151]">
              Gramtek's WhatsApp integration is <strong>informational</strong>, focusing on one-way broadcasts and simple feedback collection, ensuring compliance and ease of use.
            </p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-[#1E3A8A] text-center">
            Platform Capabilities
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 border-2 border-transparent hover:border-[#25D366]">
                  <Icon className="w-12 h-12 text-[#25D366] mb-4" />
                  <h3 className="text-2xl font-semibold mb-3 text-[#1E3A8A]">{feature.title}</h3>
                  <p className="text-lg text-[#374151] leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-4 text-[#1E3A8A] text-center">
            Real-World Applications
          </h2>
          <p className="text-xl text-center mb-12 text-[#374151] max-w-3xl mx-auto">
            See how WhatsApp integration enhances your governance and constituent engagement.
          </p>
          
          <div className="space-y-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-[#F9FAFB] p-8 rounded-lg border-2 border-[#E5E7EB] hover:border-[#25D366] transition-colors duration-300">
                <h3 className="text-2xl font-semibold mb-3 text-[#1E3A8A]">{useCase.title}</h3>
                <p className="text-lg text-[#374151] mb-4 leading-relaxed">{useCase.description}</p>
                <div className="bg-white p-4 rounded border-l-4 border-[#25D366]">
                  <p className="text-base text-[#374151] italic">{useCase.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Benefits */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-[#1E3A8A] text-center">
            Strategic Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">For Constituents</h3>
              <ul className="space-y-3 text-[#374151]">
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] font-bold mt-1">✓</span>
                  <span>Instant access to important information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] font-bold mt-1">✓</span>
                  <span>No need to download new apps or learn new platforms</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] font-bold mt-1">✓</span>
                  <span>Easy feedback and query submission</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] font-bold mt-1">✓</span>
                  <span>Feel personally connected to leadership</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4 text-[#1E3A8A]">For Public Servants</h3>
              <ul className="space-y-3 text-[#374151]">
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] font-bold mt-1">✓</span>
                  <span>Reach 95%+ of your database instantly</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] font-bold mt-1">✓</span>
                  <span>Cost-effective compared to SMS or print media</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] font-bold mt-1">✓</span>
                  <span>Real-time analytics on message delivery and engagement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#25D366] font-bold mt-1">✓</span>
                  <span>Build reputation as an accessible, modern leader</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-semibold mb-6">
            Start Connecting on WhatsApp
          </h2>
          <p className="text-xl mb-8">
            Be where your constituents are. Build trust through direct, timely, and relevant communication.
          </p>
          <Button 
            size="lg"
            className="bg-[#EA580C] hover:bg-[#DC4D09] text-white px-12 py-6 text-xl"
            asChild
          >
            <Link to="/contact">Learn More</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default WhatsAppPage;